import { FC, useEffect, useRef, useState } from 'react';
import moment from 'moment';

import DefaultModal from 'components/Modals/DefaultModal';
import CommonButton from 'components/CommonButton/CommonButton';
import SelectHoursRange from 'components/SelectHoursRange/SelectHoursRanger';

import SelectType from 'components/SelectType/SelectType';
import DateRange, { IDateRange } from 'components/DateRange/DateRange';
import ApplyOption from 'components/ApplyOption/ApplyOption';

import { DEFAULT_WORKING_HOUR, NEXT_23_MONTH } from 'utils/constants';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import { WeekWorkingHour, WorkingHour } from 'interfaces/clinicSchedule';
import {
  getInitialType,
  getInitialWorkingHours,
} from 'utils/getInitialEditScheduleState';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import useClinicDateOff from 'hooks/useClinicDateOff';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import useMatchMutate from 'hooks/useMatchMutate';
import { WeeklyTimeslot } from 'pages/OfficeSchedulePage/WrapperSchedulePage';
import getQueryTimeSlotPerDay from 'utils/getQueryTimeSlotPerDay';
import { PractitionerException } from 'interfaces/practitionerException';
import OverrideModal from '../OverrideModal/OverrideModal';
import { IPractitioner } from 'interfaces/practitioners';
import { PractitionerSchedule } from 'interfaces/practitionerSchedule';
import useModal from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import OperatoryDropdown from '../../PractitionerProfile/OperatoryDropdown/OperatoryDropdown';
import { Option } from '../../PractitionerProfile/MultipleSelect/MultipleSelect';
import usePractitionerSettings from 'hooks/usePractitionerSettings';
import LunchBreakSection from './LunchBreakSection/LunchBreakSection';
import { convertLunchBreaks, LunchBreakItem } from 'utils/normalizeEventBreak';
import checkLunchBreakAndWorkingHour from '../../utils/checkLunchBreakAndWorkingHour';

interface EditScheduleModalProps {
  selectedDate: {
    date: string;
    workingHour: string | WorkingHour[];
    lunchBreaks: WorkingHour[];
  };
  onClose: () => void;
  selectedPractitioner: IPractitioner;
  onCheckOutsidePractitionerHourAppts: () => Promise<void>;
  version: number;
  isCreatedMode?: boolean;
  practitionerSchedule?: PractitionerSchedule;
}

const options = [
  { label: 'Working Day', isWorkingDay: true },
  { label: 'Day Off', isWorkingDay: false },
];

interface EditScheduleForm {
  operatories: Option[];
  lunchBreaks: LunchBreakItem;
}

const EditScheduleModal: FC<EditScheduleModalProps> = ({
  selectedDate,
  onClose,
  selectedPractitioner,
  onCheckOutsidePractitionerHourAppts,
  version,
  isCreatedMode,
  practitionerSchedule,
}) => {
  const url = `/practitioners/${selectedPractitioner.id}/exceptions`;

  const { data: clinicDateOff, isLoading: isClinicDateOffLoading } =
    useClinicDateOff({
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().add(NEXT_23_MONTH, 'months').format('YYYY-MM-DD'),
      isClosed: true,
    });

  const {
    data: practitionerSettings,
    isLoading: isPractitionerSettingsLoading,
  } = usePractitionerSettings(selectedPractitioner.id);

  const isLoading = isClinicDateOffLoading || isPractitionerSettingsLoading;

  const {
    control,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<EditScheduleForm>({
    defaultValues: {
      operatories: [],
      lunchBreaks: {},
    },
    mode: 'all',
  });

  useEffect(() => {
    if (!practitionerSettings) return;

    const isOperatoriesAssigned =
      practitionerSchedule?.operatories &&
      practitionerSchedule.operatories.length >= 0;

    let operatories = practitionerSettings.operatories.map(
      (operatory: any) => ({
        label: operatory.name,
        value: operatory.id,
      })
    );

    if (isOperatoriesAssigned && practitionerSettings.isAdvancedSchedule) {
      operatories = practitionerSchedule.operatories?.map((item) => ({
        label: item.name,
        value: item.id,
      }));
    }

    const lunchBreaks = convertLunchBreaks(selectedDate.lunchBreaks);

    reset({ operatories, lunchBreaks });
  }, [
    practitionerSchedule?.operatories,
    practitionerSettings,
    reset,
    selectedDate.lunchBreaks,
  ]);

  const { setIsAllowed } = useModal({ onClose, isLoading });

  const matchMutate = useMatchMutate();

  const [selectedType, setSelectedType] = useState(() =>
    getInitialType(selectedDate.workingHour, options)
  );

  const [isSingleDay, setIsSingleDay] = useState(true);

  const [workingHours, setWorkingHours] = useState(() =>
    getInitialWorkingHours(selectedDate.workingHour)
  );

  const [selectedSingleDate, setSelectedSingleDate] = useState(() =>
    moment(selectedDate.date).toDate()
  );

  const [dateRange, setDateRange] = useState<IDateRange>(() => ({
    dateFrom: moment(selectedDate.date).toDate(),
    dateTo: null,
  }));

  const dateRangePickerRef = useRef<HTMLButtonElement | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [exceptionDates, setExceptionDates] = useState<
    PractitionerException[] | null
  >(null);

  const handleUpdateWorkingHours = (_: string, value: string, type: string) => {
    setWorkingHours({ ...workingHours, [type]: value });
    const lunchBreaks = watch('lunchBreaks');

    const result = checkLunchBreakAndWorkingHour({
      lunchBreaks,
      type: type as 'start' | 'end',
      value,
    });

    if (result.isStartLaterThanLunchBreakStart) {
      setValue('lunchBreaks', { ...lunchBreaks, startFirst: value });
    }

    if (result.isEndEarlierThanLunchBreakEnd) {
      setValue('lunchBreaks', { ...lunchBreaks, endFirst: value });
    }
  };

  useEffect(() => {
    if (!clinicDateOff) return;

    const isOfficeClosedIncludeDateFrom = !!clinicDateOff.find(
      (d) => d.date === moment(dateRange.dateFrom).format('YYYY-MM-DD')
    );

    if (isOfficeClosedIncludeDateFrom) {
      setDateRange((dateRange) => ({ ...dateRange, dateFrom: null }));
    }
  }, [clinicDateOff, dateRange.dateFrom]);

  const getButtonDisabledState = () => {
    const isDayRangeOptionNotSelect = !dateRange.dateTo && !isSingleDay;

    const isSelectedDateOfficeClosed = isSingleDay
      ? !!clinicDateOff?.find(
          (d) => d.date === moment(selectedSingleDate).format('YYYY-MM-DD')
        )
      : !!clinicDateOff?.find(
          (d) => d.date === moment(dateRange.dateFrom).format('YYYY-MM-DD')
        );

    const isDisabled = isDayRangeOptionNotSelect || isSelectedDateOfficeClosed;

    if (!practitionerSettings.isAdvancedSchedule) {
      return isDisabled;
    }

    return isDisabled || !isValid;
  };

  const getExcludeDates = () => {
    if (!clinicDateOff) return [];
    return clinicDateOff.map((d) => moment(d.date).toDate());
  };

  const getChangedTimeSlots = () => {
    const changedTimeSlots: WeeklyTimeslot[] = [];

    const updateTimeSlot = {
      start: selectedType.isWorkingDay ? workingHours.start : null,
      end: selectedType.isWorkingDay ? workingHours.end : null,
    };

    changedTimeSlots.push({
      ...updateTimeSlot,
      day: moment(dateRange.dateFrom).format('ddd').toLowerCase(),
    });

    const endDay = moment(dateRange.dateTo);

    // GET DAY RANGE
    const daysPerWeek = 7;
    for (let i = 1; i < daysPerWeek; i++) {
      const nextDay = moment(dateRange.dateFrom).add(i, 'd');
      if (nextDay.isSameOrBefore(endDay)) {
        const day = nextDay.format('ddd').toLowerCase();
        changedTimeSlots.push({ ...updateTimeSlot, day });
      }
    }

    return changedTimeSlots;
  };

  const onUpdatedSchedule = async () => {
    const body = [];

    const startDate = moment(
      isSingleDay ? selectedSingleDate : dateRange.dateFrom
    );

    const { data: practitionerExceptions } = await axiosInstance.get<
      PractitionerException[]
    >(url, {
      params: {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: moment(dateRange.dateTo).format('YYYY-MM-DD'),
      },
    });

    const officeClosedDates = clinicDateOff!.map(
      (officeClosedDate) => officeClosedDate.date
    );

    for (let i = 0; i < practitionerExceptions.length; i++) {
      const isExcpetionDate = practitionerExceptions[i].isException;

      const isCurrentDateOfficeClosed = officeClosedDates.includes(
        practitionerExceptions[i].date
      );

      if (isExcpetionDate || isCurrentDateOfficeClosed) continue;

      const day = {
        date: practitionerExceptions[i].date,
        isClosed: !selectedType.isWorkingDay,
        shifts: [
          {
            start: selectedType.isWorkingDay ? workingHours.start : null,
            end: selectedType.isWorkingDay ? workingHours.end : null,
          },
        ],
        ...(selectedType.isWorkingDay && {
          operatoryIds: watch('operatories').map((item) => item.value),
        }),
        ...(selectedType.isWorkingDay &&
          convertLunchBreakForBE() && {
            lunchBreaks: convertLunchBreakForBE(),
          }),
      };
      body.push(day);
    }

    if (body.length === 0) return;

    await axiosInstance.post(url, body, {
      headers: {
        version,
      },
    });
  };

  const getLunchBreakParams = () => {
    const lunchBreaks = convertLunchBreakForBE() ?? [];

    if (lunchBreaks.length === 0) return {};

    if (lunchBreaks.length === 1)
      return {
        firstLunchBreakStart: lunchBreaks[0].start,
        firstLunchBreakEnd: lunchBreaks[0].end,
      };

    return {
      firstLunchBreakStart: lunchBreaks[0].start,
      firstLunchBreakEnd: lunchBreaks[0].end,
      secondLunchBreakStart: lunchBreaks[1].start,
      secondLunchBreakEnd: lunchBreaks[1].end,
    };
  };

  const handleExceptionChecked = async () => {
    setIsSubmitting(true);
    const changedTimeSlots = getChangedTimeSlots();

    const queryTimeSlotPerDay = getQueryTimeSlotPerDay(changedTimeSlots);

    const lunchBreakParams = getLunchBreakParams();

    try {
      const response = await axiosInstance.get<PractitionerException[]>(
        `/practitioners/${selectedPractitioner.id}/custom-days`,
        {
          params: {
            startDate: moment(dateRange.dateFrom).format('YYYY-MM-DD'),
            endDate: moment(dateRange.dateTo).format('YYYY-MM-DD'),
            ...queryTimeSlotPerDay,
            ...lunchBreakParams,
            operatoryIds: watch('operatories').map((item) => item.value),
          },
        }
      );

      const isExceptionDateFound = response.data.length > 0;

      const officeClosedDates = clinicDateOff!.map(
        (officeClosedDate) => officeClosedDate.date
      );

      const nonOfficeClosedDates = response.data.filter(
        (exceptionDate) => !officeClosedDates.includes(exceptionDate.date)
      );

      if (isExceptionDateFound && nonOfficeClosedDates.length > 0) {
        setIsSubmitting(false);
        setExceptionDates(nonOfficeClosedDates);
        return;
      }

      await onUpdatedSchedule();

      await Promise.all([
        matchMutate(/\/exceptions\?[\s\S]+/),
        matchMutate(/\/custom-days\?[\s\S]+/),
        onCheckOutsidePractitionerHourAppts(),
      ]);
      renderToast({
        message: 'Custom hours have been updated successfully',
        type: 'success',
      });

      onClose();
    } catch (error) {
      renderToast({
        message: 'An error has occurred. Please try again.',
        type: 'error',
      });
    }
    setIsSubmitting(false);
  };

  const convertLunchBreakForBE = () => {
    const lunchBreaks = watch('lunchBreaks');

    const isLunchBreaksValid = lunchBreaks.startFirst && lunchBreaks.endFirst;

    if (!isLunchBreaksValid) return null;

    const lunchBreaksPayload = [
      { start: lunchBreaks.startFirst, end: lunchBreaks.endFirst },
    ];

    if (lunchBreaks.startSecond && lunchBreaks.endSecond) {
      lunchBreaksPayload.push({
        start: lunchBreaks.startSecond,
        end: lunchBreaks.endSecond,
      });
    }

    return lunchBreaksPayload;
  };

  const handleUpdateSingle = async () => {
    setIsSubmitting(true);

    try {
      // CHECK IF SELECTED DAY IS CUSTOM DAY OF NOT
      const response = await axiosInstance.get(url, {
        params: {
          startDate: moment(selectedSingleDate).format('YYYY-MM-DD'),
          endDate: moment(selectedSingleDate)
            .add(1, 'days')
            .format('YYYY-MM-DD'),
        },
      });

      const exceptionDateId = response.data[0]?.id;

      const body = [
        {
          date: moment(selectedSingleDate).format('YYYY-MM-DD'),
          isClosed: !selectedType.isWorkingDay,
          shifts: [
            {
              start: selectedType.isWorkingDay ? workingHours.start : null,
              end: selectedType.isWorkingDay ? workingHours.end : null,
            },
          ],
          ...(exceptionDateId && { id: exceptionDateId }),
        },
      ];

      if (
        selectedType.isWorkingDay &&
        practitionerSettings.isAdvancedSchedule
      ) {
        body.forEach((item) => {
          item.operatoryIds = watch('operatories').map((item) => item.value);
        });
      }

      const lunchBreaksPayload = convertLunchBreakForBE();

      if (lunchBreaksPayload) {
        body.forEach((item) => {
          item.lunchBreaks = lunchBreaksPayload;
        });
      }

      if (exceptionDateId) {
        await axiosInstance.patch(url, body, {
          headers: {
            version,
          },
        });
      } else {
        await axiosInstance.post(url, body, {
          headers: {
            version,
          },
        });
      }

      await Promise.all([
        matchMutate(/\/exceptions\?[\s\S]+/),
        matchMutate(/\/custom-days\?[\s\S]+/),
        onCheckOutsidePractitionerHourAppts(),
      ]);

      renderToast({
        message: 'Custom hours have been updated successfully',
        type: 'success',
      });

      onClose();
    } catch (error) {
      renderToast({
        message: 'An error has occurred. Please try again.',
        type: 'error',
      });
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setIsAllowed(false);
    if (!isSingleDay) {
      await handleExceptionChecked();
      return;
    }
    await handleUpdateSingle();
  };

  const getWorkingHoursForExceptionClosedDate = () => {
    if (!practitionerSchedule) {
      return;
    }

    const date = moment(selectedDate.date).format('ddd').toLowerCase();

    const value = practitionerSchedule.workingHour[
      date as keyof WeekWorkingHour
    ]![0] as WorkingHour;

    return {
      start: value.start ? value.start : DEFAULT_WORKING_HOUR.START,
      end: value.end ? value.end : DEFAULT_WORKING_HOUR.END,
    };
  };

  const onChangeWorkingType = (value: any) => {
    if (!value.isWorkingDay) {
      setValue(
        'lunchBreaks',
        {
          ...watch('lunchBreaks'),
          startFirst: null,
          endFirst: null,
          startSecond: null,
          endSecond: null,
        },
        { shouldValidate: true }
      );
    }
    setSelectedType(value);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return exceptionDates ? (
    <OverrideModal
      exceptionDates={exceptionDates as any}
      changedTimeSlots={getChangedTimeSlots()}
      selectedPractitioner={selectedPractitioner}
      onUpdateSchedule={onUpdatedSchedule}
      onClose={() => {
        setExceptionDates(null);
        onClose();
      }}
      onBtnClose={() => setExceptionDates(null)}
      version={version}
      onCheckOutsidePractitionerHourAppts={onCheckOutsidePractitionerHourAppts}
      operatories={watch('operatories')}
      isAdvancedSchedule={practitionerSettings.isAdvancedSchedule}
      lunchBreaks={convertLunchBreakForBE()}
    />
  ) : (
    <DefaultModal className="top-[45%]">
      <div className="w-[487px] min-h-[290px] pl-3.2 pr-4.4 pt-2.4 pb-3.2 text-14 leading-[2.1rem] text-darkest-grey relative">
        <h3 className="text-16 leading-[2.4rem] font-bold">
          {isCreatedMode ? 'Add custom day(s)' : 'Edit practitioner schedule'}
        </h3>
        <div className="flex items-center mt-1.5 ">
          <span className="text-13 leading-[2rem]">Type</span>
          <div className="flex-1 w-full ml-8">
            <SelectType
              selectedType={selectedType}
              setSelectedType={onChangeWorkingType}
              options={options}
            />
          </div>
        </div>
        <div className="mt-2.7">
          <div
            className={`flex items-center ${
              !selectedType.isWorkingDay ? 'opacity-30 relative -z-10' : ''
            }`}
          >
            <span className="text-13 leading-[2rem]">Hours</span>
            <div className="flex-1 ml-8 all-child:justify-between">
              <SelectHoursRange
                day={selectedDate.date}
                defaultValue={{
                  start:
                    typeof selectedDate.workingHour === 'string'
                      ? getWorkingHoursForExceptionClosedDate()!.start
                      : workingHours.start,
                  end:
                    typeof selectedDate.workingHour === 'string'
                      ? getWorkingHoursForExceptionClosedDate()!.end
                      : workingHours.end,
                }}
                start={
                  typeof selectedDate.workingHour === 'string'
                    ? getWorkingHoursForExceptionClosedDate()!.start
                    : workingHours.start
                }
                end={
                  typeof selectedDate.workingHour === 'string'
                    ? getWorkingHoursForExceptionClosedDate()!.end
                    : workingHours.end
                }
                onChange={handleUpdateWorkingHours}
              />
            </div>
          </div>
        </div>
        <div className="mt-1.2">
          <LunchBreakSection
            workingHours={workingHours}
            isWorkingDay={selectedType.isWorkingDay}
            lunchBreaks={watch('lunchBreaks')}
            onChange={(updatedLunchBreaks) =>
              setValue('lunchBreaks', updatedLunchBreaks)
            }
          />
        </div>
        <div className="mt-2.9">
          <div className="flex items-center">
            <span className="text-13 leading-[2rem]">Apply to</span>
            <div className="flex-1 ml-4.5">
              <ApplyOption
                isSingleDay={isSingleDay}
                setIsSingleDay={setIsSingleDay}
                dateTo={dateRange.dateTo}
                dateRangePickerRef={dateRangePickerRef}
              />
            </div>
          </div>
        </div>

        <div className="mt-1.9 flex">
          {isSingleDay && (
            <CustomDatePicker
              excludeDates={getExcludeDates()}
              selected={selectedSingleDate}
              onChange={(date: Date | null) => {
                if (date) {
                  setSelectedSingleDate(date);
                }
              }}
              minDate={new Date()}
              maxDate={moment().add(NEXT_23_MONTH, 'months').toDate()}
              customInput={
                <div className="flex items-center gap-x-7.8">
                  <span className="text-13 leading-[2rem]">Date</span>
                  <button
                    className={`w-full h-3 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active px-1.6 ml-2.4`}
                  >
                    {moment(selectedSingleDate).format('MMMM DD, YYYY')}
                  </button>
                </div>
              }
              popperModifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [116, -2],
                  },
                },
              ]}
            />
          )}
          <div className={isSingleDay ? 'hidden' : 'flex even:text-red'}>
            <DateRange
              dateRange={dateRange}
              setDateRange={setDateRange}
              dateRangePickerRef={dateRangePickerRef}
              excludeDates={getExcludeDates()}
            />
          </div>
        </div>

        {practitionerSettings.isAdvancedSchedule && (
          <div
            className={`mt-1.9 flex gap-x-5.8 ${
              !selectedType.isWorkingDay
                ? 'pointer-events-none opacity-30 '
                : ''
            }`}
          >
            <span className="text-13 leading-[2rem] pt-0.5">Opeartories</span>
            <div className={`flex-1 `}>
              <OperatoryDropdown
                controlStyles={{ height: '3rem', minHeight: '0' }}
                control={control}
                options={
                  practitionerSettings?.operatories.map((item: any) => ({
                    value: item.id,
                    label: item.name,
                  })) ?? []
                }
                rules={{
                  validate: (value) =>
                    value.length > 0 ? true : 'This field is required',
                }}
              />
            </div>
          </div>
        )}

        <div className="mt-2.8 flex gap-x-1.6 justify-end">
          <CommonButton
            variant="secondary"
            className="!min-w-[13rem] !min-h-[3.2rem]"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </CommonButton>
          <CommonButton
            className="!min-w-[13rem] !min-h-[3.2rem]"
            disabled={getButtonDisabledState()}
            onClick={handleConfirm}
            isLoading={isSubmitting}
          >
            Confirm
          </CommonButton>
        </div>
      </div>
    </DefaultModal>
  );
};

export default EditScheduleModal;
